import pipefitterApi from '../../../services/pipefitterApi';
import type { ReportHistoryReportData } from './reportHistory';

export interface PipeDataPreviewParams {
  projectNumber: string;
  pipeId?: string | number;
  projectId?: string | number;
  selectedPipeStatusIds: (string | number)[];
  /** Scope preview to this upload group (matches pipestatus/pipeinfo.groupID). */
  groupId?: string | null;
}

/**
 * Load pipe-data preview for the generate modal.
 * Uses POST so large selections (1000+ status ids) are not put in the query string
 * (API Gateway rejects oversized URLs).
 */
export async function fetchPipeDataPreview(params: PipeDataPreviewParams): Promise<unknown> {
  const { projectNumber, pipeId, projectId, selectedPipeStatusIds, groupId } = params;

  const body: Record<string, unknown> = {};
  if (pipeId != null && pipeId !== '') body.pipe_id = Number(pipeId) || pipeId;
  if (projectId != null && projectId !== '') body.projectId = Number(projectId) || projectId;
  if (selectedPipeStatusIds.length > 0) {
    body.selectedPipeStatusIds = selectedPipeStatusIds
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n));
  }
  if (groupId !== undefined && groupId !== null) {
    body.groupId = String(groupId).trim();
  }

  const { data } = await pipefitterApi.post(
    `/reportgen/pipe-data-preview/${encodeURIComponent(projectNumber)}`,
    body
  );

  const ok = data?.status === 200 || data?.success === 1;
  if (!ok) {
    throw new Error(data?.message || 'Failed to load pipe data preview');
  }

  return data?.data ?? data;
}

export function normalizePipeDataPreview(raw: unknown): ReportHistoryReportData | null {
  if (!raw || typeof raw !== 'object') return null;

  const record = raw as Record<string, unknown>;

  if (record.reportData && typeof record.reportData === 'object') {
    return record.reportData as ReportHistoryReportData;
  }

  if (Array.isArray(record.pipesList)) {
    return record as ReportHistoryReportData;
  }

  if (record.data && typeof record.data === 'object') {
    return normalizePipeDataPreview(record.data);
  }

  return null;
}
